import { Controller } from "@hotwired/stimulus";
import { Collapse } from "bootstrap";

// Connects to data-controller="provider-form"
export default class extends Controller {
  static values = {
    shouldFetchOnCollapse: { type: Boolean, default: false },
    initialShipmentDate: String,
    firstShipmentDate: String,
    filterType: String,
    errorMessage: String,
  };

  static targets = [
    "shipmentCollapse",
    "providerId",
    "providerName",
    "productLocalCutoffDateAndTime",
    "productCode",
    "productName",
    "productPrice",
    "pickupServiceDate",
    "pickupEarliestTime",
    "pickupLatestTime",
    "deliveryServiceDate",
    "provider",
    "currentFilter",
    "filterList",
    "shipmentCollapseButton",
    "turboFrame",
  ];

  connect() {
    // TODO: Instead of data-attributes and form, keep this in the shipment_form session. hidden from the user.
    const shouldToggle = this.firstShipmentDateValue !== this.initialShipmentDateValue;

    this.collapseInstance = new Collapse(this.shipmentCollapseTarget, {
      toggle: shouldToggle,
    });

    if (shouldToggle) {
      this.updateAriaExpanded();
      this.shipmentCollapseTarget.classList.add("no-transitions");
      this.shipmentCollapseTarget.addEventListener("shown.bs.collapse", () => {
        this.shipmentCollapseTarget.classList.remove("no-transitions");
      });
    }

    document.addEventListener("turbo:before-stream-render", (event) => {
      if (event.target.target != "providers") return;

      const originalRender = event.detail.render;

      const self = this;
      event.detail.render = function (streamElement) {
        originalRender(streamElement);

        self.sortProviders();
      };
    });
  }

  submit(event) {
    event.preventDefault();

    const providerTarget = event.currentTarget.closest(".provider--wrapper");

    if (!providerTarget) return;

    this.providerIdTarget.value = providerTarget.dataset.providerId;
    this.providerNameTarget.value = providerTarget.dataset.providerName;
    this.productLocalCutoffDateAndTimeTarget.value = providerTarget.dataset.productLocalCutoffDateAndTime;
    this.productCodeTarget.value = providerTarget.dataset.productCode;
    this.productNameTarget.value = providerTarget.dataset.productName;
    this.productPriceTarget.value = providerTarget.dataset.productPrice;
    this.pickupServiceDateTarget.value = providerTarget.dataset.pickupServiceDate;
    this.pickupEarliestTimeTarget.value = providerTarget.dataset.pickupEarliestTime;
    this.pickupLatestTimeTarget.value = providerTarget.dataset.pickupLatestTime;
    this.deliveryServiceDateTarget.value = providerTarget.dataset.deliveryServiceDate;

    event.currentTarget.closest("form")?.submit();
  }

  toggleShipmentCollapse(event) {
    if (!this.shouldFetchOnCollapseValue) {
      event.preventDefault(); // Incase the collapse is a link and causes (turbo) navigation
    }

    this.updateAriaExpanded();

    this.collapseInstance.toggle();

    this.shouldFetchOnCollapseValue = false;
  }

  toggleCollapseFetchValue({ params: { shipmentDate } }) {
    // When the shipment date changes, we want to determine if we should fetch new data
    this.shouldFetchOnCollapseValue = shipmentDate !== this.firstShipmentDateValue;
  }

  updateAriaExpanded() {
    const ariaExpanded = this.shipmentCollapseButtonTarget.getAttribute("aria-expanded") === "true";
    this.shipmentCollapseButtonTarget.setAttribute("aria-expanded", !ariaExpanded);
  }

  updateFilter(event) {
    this.currentFilterTarget.innerText = event.currentTarget.innerText;

    this.filterListTarget.querySelectorAll(".disabled").forEach((filterOption) => {
      filterOption.classList.remove("disabled");
    });

    event.currentTarget.classList.add("disabled");

    this.filterTypeValue = event.params.filter;
  }

  filterTypeValueChanged(newValue, oldValue) {
    if (newValue !== oldValue) {
      window.history.replaceState(null, "", `?filter=${newValue}`);
      this.sortProviders();
    }
  }
  sortProviders() {
    if (this.providerTargets.length === 0) return;

    const newOrder = [...this.providerTargets];

    switch (this.filterTypeValue) {
      case "provider":
        newOrder.sort((a, b) => {
          const providerA = a.dataset.providerName.toLowerCase();
          const providerB = b.dataset.providerName.toLowerCase();

          if (providerA < providerB) return -1;
          if (providerA > providerB) return 1;
          return 0;
        });
        break;
      case "price":
        newOrder.sort((a, b) => {
          const priceA = parseFloat(a.dataset.productPrice);
          const priceB = parseFloat(b.dataset.productPrice);

          return priceA - priceB;
        });
        break;
      case "duration":
        newOrder.sort((a, b) => {
          const durationA = parseInt(a.dataset.duration);
          const durationB = parseInt(b.dataset.duration);

          return durationA - durationB;
        });
        break;
      case "date":
        newOrder.sort((a, b) => {
          const dateA = new Date(a.dataset.deliveryServiceDate);
          const dateB = new Date(b.dataset.deliveryServiceDate);

          return dateA - dateB;
        });
        break;
      default:
        window.dispatchEvent(new CustomEvent("flash:create", { detail: { type: "danger", message: this.errorMessageValue } }));
        break;
    }

    this.turboFrameTarget.innerHTML = "";
    newOrder.forEach((provider) => {
      this.turboFrameTarget.appendChild(provider);
    });
  }
}
